<template>
  <nav
    class="c-navbar u-color-primary"
    :class="{ 'search-visible': searchVisible }"
  >
    <div
      v-if="isMobile"
      class="c-navbar__container c-navbar__container--mobile"
      :class="{ 'c-navbar__container--transparent': pageHasFirstHero }"
    >
      <i
        v-accessibility-focus="showDrawer"
        class="o-icon o-icon--menu o-typography--action"
      ></i>
      <div class="c-navbar__wrapper c-navbar__wrapper--left">
        <ui-navbar-logo></ui-navbar-logo>
      </div>
      <div class="c-navbar__wrapper c-navbar__wrapper--right">
        <ui-navbar-search
          v-if="!isLightMenu"
          :search-visible="searchVisible"
          :initial-search-value="searchValue"
          @toggle="searchVisible = !searchVisible"
          @input="setSearchValue"
        ></ui-navbar-search>
      </div>
    </div>
    <div
      v-else
      class="c-navbar__container c-navbar__container--desktop"
      :class="{ 'c-navbar__container--desktop-transparent': pageHasFirstHero }"
    >
      <div class="c-navbar__wrapper c-navbar__wrapper--left">
        <ui-navbar-logo></ui-navbar-logo>
        <div
          ref="wrapper"
          class="c-navbar__menu-wrapper"
        >
          <template v-for="({ item, key }) in keyedMenuItems">
            <h2 v-if="isItemVisible(item)">
              <ui-menu-item
                :key="key"
                :ref="`menuItem_${key}`"
                :item="item"
                @load="delayedAdjustMenu"
              ></ui-menu-item>
            </h2>
          </template>
        </div>
        <div
          v-if="keyedMenuHiddenItems.length"
          class="c-navbar__menu-more u-desktop-only"
        >
          <div class="o-separator o-separator--accent"></div>
          <v-dropdown class="c-navbar__menu-dropdown">
            <template #activator>
              <div class="o-typography--action c-navbar__menu-dropdown-activator">
                <span class="u-mr-5">{{ $t('common.more') }}</span>
                <i class="o-icon o-icon--down o-icon--xsmall c-navbar__icon"></i>
              </div>
            </template>
            <template v-for="({ item, key }) in keyedMenuHiddenItems">
              <ui-menu-item
                v-if="isItemVisible(item)"
                :key="key"
                :item="item"
              ></ui-menu-item>
            </template>
          </v-dropdown>
        </div>
        <v-toggle
          v-if="!isLightMenu"
          v-model="kidsMode"
          :label="$t('common.kids')"
          :title="$t('common.kids')"
          class="u-inline-block"
        ></v-toggle>
      </div>
      <div class="c-navbar__wrapper c-navbar__wrapper--right">
        <ui-navbar-accessibility-icon></ui-navbar-accessibility-icon>
        <ui-navbar-search
          v-if="!isLightMenu"
          :search-visible="searchVisible"
          :initial-search-value="searchValue"
          @toggle="toggleSearch"
          @input="setSearchValue"
        ></ui-navbar-search>
        <ui-navbar-subscriber @showProfiles="showProfiles"></ui-navbar-subscriber>
        <router-link
          v-if="!subscriber.isLogged"
          :to="{ name: RouteName.SUBSCRIBER_REGISTER }"
          class="o-typography--action c-navbar__button"
        >
          {{ $t('common.register') }}
        </router-link>
        <template v-if="info.languages.length > 1">
          <div class="o-separator o-separator--accent"></div>
          <ui-navbar-language></ui-navbar-language>
        </template>
      </div>
    </div>
  </nav>
</template>
<script>
  import throttle from 'lodash/throttle';
  import { EventType, RouteName } from '@/shared/enums';
  import { generateKeyedArray } from '@/shared/utils';
  import { onboardingMixin } from '@/index/shared/mixins';
  import { VDropdown, VToggle } from '@/index/shared/components';
  import UiMenuItem from './menu-item.vue';
  import UiNavbarAccessibilityIcon from './accessibility-icon.vue';
  import UiNavbarLanguage from './language.vue';
  import UiNavbarLogo from './logo.vue';
  import UiNavbarSearch from './search.vue';
  import UiNavbarSubscriber from './subscriber.vue';

  const getElementBoundary = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.x + rect.width;
  };

  export default {
    components: {
      UiMenuItem,
      UiNavbarAccessibilityIcon,
      UiNavbarLanguage,
      UiNavbarLogo,
      UiNavbarSearch,
      UiNavbarSubscriber,
      VDropdown,
      VToggle,
    },
    enums: {
      RouteName,
    },
    mixins: [
      onboardingMixin,
    ],
    data() {
      return {
        pinned: false,
        searchVisible: false,
        searchValue: '',
        menu: this.$services.resolve('menu'),
        subscriber: this.$services.resolve('subscriber'),
        info: this.$services.resolve('info'),
        menuHiddenItems: [],
      };
    },
    computed: {
      isMobile() {
        return this.$window.isMobileView;
      },
      pageHasFirstHero() {
        return this.$store.state.hero.hasFirstHero;
      },
      menuItems() {
        if (this.subscriber.isKidsTheme) {
          return this.menu.data.desktopKids;
        }

        return this.menu.data.desktop;
      },
      keyedMenuItems() {
        return generateKeyedArray(this.menuItems);
      },
      keyedMenuHiddenItems() {
        return generateKeyedArray(this.menuHiddenItems);
      },
      kidsMode: {
        get() {
          return this.$storage.isKidsMode;
        },
        set(newValue) {
          this.menuHiddenItems = [];
          this.subscriber.setKidsMode(newValue);
        },
      },
    },
    watch: {
      menuItems: 'delayedAdjustMenu',
    },
    created() {
      this.delayedAdjustMenu = throttle(this.delayedAdjustMenu, 1000);
    },
    mounted() {
      this.$EventBus.on(EventType.RESIZE, this.delayedAdjustMenu);
      this.$EventBus.on(EventType.PAGE_VIEW, this.delayedAdjustMenu);
    },
    beforeDestroy() {
      this.$EventBus.off(EventType.RESIZE, this.delayedAdjustMenu);
      this.$EventBus.off(EventType.PAGE_VIEW, this.delayedAdjustMenu);
    },
    methods: {
      setSearchValue(value) {
        this.searchValue = value;
      },
      adjustMenu() {
        if (this.isMobile) {
          return;
        }

        const wrapperBoundary = getElementBoundary(this.$refs.wrapper);
        const menuHiddenItems = [];

        this.keyedMenuItems.forEach(({ item, key }) => {
          const refs = this.$refs[`menuItem_${key}`];

          if (!refs || !refs[0]?.$el) {
            return;
          }

          const element = refs[0].$el;
          const elementBoundary = getElementBoundary(element);

          if (elementBoundary > wrapperBoundary) {
            menuHiddenItems.push(item);
            element.classList.add('u-hidden');
          } else {
            element.classList.remove('u-hidden');
          }
        });

        this.menuHiddenItems = menuHiddenItems;
      },
      async delayedAdjustMenu() {
        await this.$nextTick();
        this.adjustMenu();

        // We need to recalculate menuHiddenItems once more when c-navbar__menu-more becomes visible
        if (this.menuHiddenItems.length) {
          await this.$nextTick();
          this.adjustMenu();
        }
      },
      isItemVisible(item) {
        if (this.isLightMenu) {
          return false;
        }

        return !item.isLogged || (item.isLogged && this.subscriber.isLogged);
      },
      showDrawer() {
        this.searchVisible = false;
        this.$emit('showDrawer');
      },
      showProfiles(options) {
        this.$emit('showProfiles', options);
      },
      toggleSearch() {
        this.searchVisible = !this.searchVisible;
        setTimeout(() => this.delayedAdjustMenu(), 300);
      },
    },
  };
</script>
